import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse, httpResource } from '@angular/common/http';
import { computed, effect, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { withCallStatus } from '@ngrx-traits/signals';
import {
  patchState,
  signalStore,
  signalStoreFeature,
  withComputed,
  withFeature,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { injectApiUrl } from '@seatix/shared-config';
import { AlertService, withAlert } from '@seatix/shared-util-alert';
import { LoadingService } from '@seatix/shared-util-loading';
import { withCountdown } from '@seatix/shared-util-store-with-countdown';
import { appendAuthTransfer } from '@seatix/shared/auth/utils';
import { injectCheckoutUrl, injectFanPortalUrl } from '@seatix/shared/core';
import { AuthStore } from '@seatix/shared/data-access/auth';
import {
  catchError,
  EMPTY,
  exhaustMap,
  finalize,
  first,
  ignoreElements,
  interval,
  map,
  Observable,
  of,
  pipe,
  switchMap,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { CheckoutApiService } from './checkout-api.service';
import {
  isFailedOrderStatus,
  isOrderProcessingConflict,
  ORDER_CONFIRMATION_OUTCOME_MESSAGES,
  ORDER_CONFIRMATION_POLL_INTERVAL_MS,
  ORDER_CONFIRMATION_POLL_WINDOW_MS,
  OrderConfirmationOutcome,
} from './order-confirmation-poll';
import { OrderStorageService } from './order-storage.service';
import { PaymentReferenceStorageService } from './payment-reference-storage.service';
import { PspRedirectMarkerService } from './psp-redirect-marker.service';
import {
  AllowedPaymentMethods,
  ConektaReturnResult,
  LogoUrl,
  MercadoPagoReturnParams,
  MercadoPagoReturnStatus,
  MultiPaymentInitializeResponse,
  PaymentFeature,
  PaymentMethod,
  PaymentMethodType,
  PaymentReference,
  PspPromotion,
} from '@seatix/shared-domain-checkout';
import { ApiResponse } from '@seatix/shared-domain-utils';
import { ReferenceConfig } from '@seatix/shared-domain-promotions';
import { Order } from '@seatix/shared-domain-order';
import { RouteBuilder } from '@seatix/shared-util-routing';

export const RELEASE_SEATS_FAILED_MESSAGE =
  'No pudimos liberar tus asientos. Intenta de nuevo.';

export interface ProviderDisplayData {
  logo_urls: LogoUrl[] | null;
  subtitle: string | null;
  features: PaymentFeature[] | null;
}

export interface PaymentMethodState {
  id: PaymentMethodType;
  /** Composite identity `${provider}:${type}` — unique across providers offering the same method type. */
  key: string;
  type: PaymentMethodType;
  title: string;
  description: string;
  icon: string;
  securityIcons: string[];
  provider: string;
  min_amount: number;
  max_amount: number;
  logo_urls: LogoUrl[] | null;
  features: PaymentFeature[] | null;
  initialized: boolean;
  checkoutData?: {
    checkout_id?: string;
    client_secret?: string;
    provider_checkout_id?: string;
    provider_order_id?: string;
    reference?: string;
    redirectUrl?: string;
    status?: string;
    provider?: string;
  };
}

export interface CheckoutState {
  orderToken: string | null;
  paymentMethods: PaymentMethodState[];
  currentPaymentMethodKey: string | null;
  selectedPaymentMethod: PaymentMethod | null;
  paymentStep: 'selection' | 'payment';
  completedOrders: string[];
  paymentReference: PaymentReference | null;
  showPaymentReference: boolean;
  paymentReferenceExpiry: number | null;
  pendingExternalRedirect: boolean;
  mercadopagoFailureStatus: MercadoPagoReturnStatus | null;
  conektaReturnStatus: ConektaReturnResult | null;
  appliedCouponCode: string | null;
  pendingCouponCode: string | null;
  referenceConfig: ReferenceConfig | null;
  pspDismissedPopupTitles: string[];
  /** Set when the post-409 poll ends without a confirmation; drives the outcome panel. */
  confirmationOutcome: OrderConfirmationOutcome | null;
}

function buildPaymentMethodsFromOrder(
  allowedMethods: AllowedPaymentMethods,
): PaymentMethodState[] {
  const methods: PaymentMethodState[] = [];

  for (const group of Object.values(allowedMethods)) {
    for (const methodType of group.methods) {
      const detail = group.method_details[methodType];
      if (!detail) continue;

      methods.push({
        id: methodType as PaymentMethodType,
        key: `${group.provider}:${methodType}`,
        type: methodType as PaymentMethodType,
        title: detail.label,
        description: detail.description,
        icon: '',
        securityIcons: [],
        provider: group.provider,
        min_amount: detail.min_amount,
        max_amount: detail.max_amount,
        logo_urls: detail.logo_urls ?? null,
        features: detail.features ?? null,
        initialized: false,
      });
    }
  }

  return methods;
}

export const initialCheckoutState: CheckoutState = {
  orderToken: null,
  currentPaymentMethodKey: null,
  paymentMethods: [],
  selectedPaymentMethod: null,
  paymentStep: 'selection',
  completedOrders: [],
  paymentReference: null,
  showPaymentReference: false,
  paymentReferenceExpiry: null,
  pendingExternalRedirect: false,
  mercadopagoFailureStatus: null,
  conektaReturnStatus: null,
  appliedCouponCode: null,
  pendingCouponCode: null,
  referenceConfig: null,
  pspDismissedPopupTitles: [],
  confirmationOutcome: null,
};

export const CheckoutStore = signalStore(
  { providedIn: 'root' },
  withState<CheckoutState>(initialCheckoutState),
  signalStoreFeature(
    withCallStatus({ prop: 'payment', errorType: 'string' }),
    withCallStatus({ prop: 'paymentConfirmation', errorType: 'string' }),
    withCallStatus({ prop: 'conektaCheckout', errorType: 'string' }),
    withCallStatus({ prop: 'digitalfemsaCheckout', errorType: 'string' }),
    withCallStatus({ prop: 'coupon', errorType: 'string' }),
  ),
  withProps(() => ({
    _checkoutApi: inject(CheckoutApiService),
    _alert: inject(AlertService),
    _router: inject(Router),
    _document: inject(DOCUMENT),
    _orderStorage: inject(OrderStorageService),
    _referenceStorage: inject(PaymentReferenceStorageService),
    _pspRedirectMarker: inject(PspRedirectMarkerService),
    _apiUrl: `${injectApiUrl()}/api`,
    _fanPortalUrl: injectFanPortalUrl(),
    _checkoutUrl: injectCheckoutUrl(),
    _loadingService: inject(LoadingService),
    _authStore: inject(AuthStore),
  })),
  withProps((store) => ({
    _orderResource: httpResource<ApiResponse<Order>>(() => {
      const token = store.orderToken();
      if (!token) {
        return undefined;
      }
      const endpoint = `${store._apiUrl}/orders/${token}`;
      return endpoint;
    }),
  })),
  withProps((store) => ({
    orderResource: store._orderResource.asReadonly(),
  })),
  withComputed((store) => ({
    order: computed(() =>
      store.orderResource.hasValue() ? store.orderResource.value().data : null,
    ),
    // Only a timeout is told as text: a failed payment gets its own screen.
    confirmationOutcomeMessage: computed(() =>
      store.confirmationOutcome() === 'timeout'
        ? ORDER_CONFIRMATION_OUTCOME_MESSAGES.timeout
        : null,
    ),
    orderLoading: computed(() => store.orderResource?.isLoading()),
    orderError: computed(() => store.orderResource?.error()),
    isOrderNotFound: computed(
      () =>
        (store.orderResource?.error() as HttpErrorResponse | null)?.status ===
        404,
    ),
    isOrderForbidden: computed(
      () =>
        (store.orderResource?.error() as HttpErrorResponse | null)?.status ===
        403,
    ),
    isSessionExpired: computed(
      () =>
        (store.orderResource?.error() as HttpErrorResponse | null)?.status ===
        401,
    ),
    _expiresAt: computed(() => {
      const order = store.orderResource.hasValue()
        ? store.orderResource.value().data
        : null;
      return order ? new Date(order.expires_at) : null;
    }),
  })),
  withFeature((store) => withCountdown(store._expiresAt)),
  withComputed((store) => {
    const orderError = store.orderError;

    const currentMethodState = computed(() => {
      const key = store.currentPaymentMethodKey();
      if (!key) return null;
      return store.paymentMethods().find((m) => m.key === key) ?? null;
    });

    const activePaymentProcessor = computed(() => {
      const methodState = currentMethodState();
      if (!methodState) return null;
      return methodState.checkoutData?.provider ?? methodState.provider ?? null;
    });

    const conektaCheckoutData = computed(() => {
      if (activePaymentProcessor() !== 'conekta') return null;
      const method = currentMethodState();

      // Redirect-based flow: let redirectCheckoutData handle it
      if (method?.checkoutData?.redirectUrl) return null;

      return method?.checkoutData?.checkout_id
        ? { checkout_id: method.checkoutData.checkout_id }
        : null;
    });

    const digitalfemsaCheckoutData = computed(() => {
      if (activePaymentProcessor() !== 'digitalfemsa') return null;
      const method = currentMethodState();
      return method?.checkoutData?.checkout_id
        ? {
            checkout_id: method.checkoutData.checkout_id,
          }
        : null;
    });

    const redirectCheckoutData = computed(() => {
      const methodState = currentMethodState();
      return methodState?.checkoutData?.redirectUrl
        ? {
            redirectUrl: methodState.checkoutData.redirectUrl,
            provider: methodState.checkoutData.provider ?? methodState.provider,
          }
        : null;
    });

    const error = computed(() => orderError() || store.paymentError());

    const uiPaymentMethods = computed<PaymentMethod[]>(() => {
      return store.paymentMethods().map((method) => ({
        id: method.id,
        key: method.key,
        name: method.title,
        description: method.description,
        type: method.id,
        provider: method.provider,
        min_amount: method.min_amount,
        max_amount: method.max_amount,
        logo_urls: method.logo_urls,
        features: method.features,
      }));
    });

    const isConektaInitialized = computed(() => {
      const cardMethod = store
        .paymentMethods()
        .find((m) => m.provider === 'conekta' && m.type === 'card');
      return cardMethod?.initialized;
    });

    const isDigitalfemsaInitialized = computed(() => {
      const method = store
        .paymentMethods()
        .find((m) => m.provider === 'digitalfemsa' && m.type === 'oxxo');
      return method?.initialized;
    });

    const hasCouponApplied = computed(
      () =>
        !!store.appliedCouponCode() ||
        (store.order()?.applied_promotions?.length ?? 0) > 0,
    );
    const appliedPromoName = computed(
      () => store.order()?.applied_promotions?.[0]?.promotion_name ?? null,
    );
    const needsReference = computed(() => !!store.referenceConfig());

    const isLoading = computed(() => {
      return store.orderLoading() || store.isPaymentLoading();
    });

    const isOrderCompletedSignal = computed(() => {
      return (orderToken: string | null): boolean => {
        return orderToken
          ? store.completedOrders().includes(orderToken)
          : false;
      };
    });

    const providerPromotions = computed<Record<string, PspPromotion[]>>(() => {
      const allowedMethods = store.order()?.allowed_payment_methods;
      if (!allowedMethods) return {};
      return Object.values(allowedMethods).reduce(
        (acc, group) => {
          if (group.promotions?.length) {
            acc[group.provider] = group.promotions;
          }
          return acc;
        },
        {} as Record<string, PspPromotion[]>,
      );
    });

    const activeEmbeddedByProvider = computed<Record<string, PspPromotion[]>>(
      () => {
        return Object.entries(providerPromotions()).reduce(
          (acc, [provider, promos]) => {
            const embedded = promos.filter((p) => p.type === 'embedded');
            if (embedded.length) acc[provider] = embedded;
            return acc;
          },
          {} as Record<string, PspPromotion[]>,
        );
      },
    );

    const providerDisplayData = computed<Record<string, ProviderDisplayData>>(
      () => {
        const allowedMethods = store.order()?.allowed_payment_methods;
        if (!allowedMethods) return {};
        return Object.values(allowedMethods).reduce(
          (acc, group) => {
            acc[group.provider] = {
              logo_urls: group.logo_urls ?? null,
              subtitle: group.subtitle ?? null,
              features: group.features ?? null,
            };
            return acc;
          },
          {} as Record<string, ProviderDisplayData>,
        );
      },
    );

    const activePopupPromotion = computed<PspPromotion | null>(
      () => {
        const dismissed = store.pspDismissedPopupTitles();
        const allPromos = Object.values(providerPromotions()).flat();
        return (
          allPromos.find(
            (p) => p.type === 'popup' && !dismissed.includes(p.title),
          ) ?? null
        );
      },
      {
        equal: (a, b) =>
          (a?.type === 'popup' ? a.title : null) ===
          (b?.type === 'popup' ? b.title : null),
      },
    );

    const isPaymentFailed = computed(
      () => store.confirmationOutcome() === 'cancelled',
    );

    return {
      isPaymentFailed,
      currentMethodState,
      activePaymentProcessor,
      conektaCheckoutData,
      digitalfemsaCheckoutData,
      redirectCheckoutData,
      error,
      uiPaymentMethods,
      isOrderCompletedSignal,
      isLoading,
      isConektaInitialized,
      isDigitalfemsaInitialized,
      hasCouponApplied,
      appliedPromoName,
      needsReference,
      providerPromotions,
      activeEmbeddedByProvider,
      activePopupPromotion,
      providerDisplayData,
    };
  }),
  withMethods((store) => {
    function buildFanPortalUrl(path: string): string {
      return appendAuthTransfer(
        `${store._fanPortalUrl}${path}`,
        store._authStore.user(),
        store._authStore.current_team(),
      );
    }

    /**
     * Where a buyer goes when they leave the checkout without paying — an
     * expired hold, a confirmed exit, or a return from the PSP.
     *
     * Back to picking seats for whatever they were buying, which for a season
     * pass is the season's own map. The season is identified by `season_id`
     * rather than the nested `season`, because the order carries the id whether
     * or not the API is populating the object yet.
     *
     * The fan portal home is the last resort, not the default: landing there
     * silently strands a buyer who still has a pass to choose seats for.
     */
    function returnUrl(order: Order | null | undefined): string {
      if (order?.event?.slug) {
        return buildFanPortalUrl(
          RouteBuilder.events.seatSelection(order.event.slug),
        );
      }

      const seasonId = order?.season_id ?? order?.season?.id;
      if (seasonId) {
        return buildFanPortalUrl(RouteBuilder.seasons.seatSelection(seasonId));
      }

      return buildFanPortalUrl('');
    }

    function buildConektaRedirectUrls():
      { success_url: string; cancel_url: string } | undefined {
      const token = store.orderToken();
      if (!token) return undefined;
      const base = store._checkoutUrl;
      return {
        success_url: `${base}/${token}?conekta_result=success`,
        cancel_url: `${base}/${token}?conekta_result=cancel`,
      };
    }

    function setOrderToken(token: string) {
      patchState(store, { orderToken: token });
    }

    function clearOrder() {
      patchState(store, {
        orderToken: null,
        currentPaymentMethodKey: null,
        paymentMethods: [],
        selectedPaymentMethod: null,
        pendingExternalRedirect: false,
      });
    }

    function updatePaymentMethodState(
      methodKey: string,
      updates: Partial<PaymentMethodState>,
    ) {
      patchState(store, (state) => ({
        paymentMethods: state.paymentMethods.map((method) =>
          method.key === methodKey ? { ...method, ...updates } : method,
        ),
      }));
    }

    function setPaymentMethod(methodKey: string | null) {
      patchState(store, { currentPaymentMethodKey: methodKey });
    }

    function markOrderCompleted(orderToken: string) {
      patchState(store, (state) => ({
        completedOrders: [...state.completedOrders, orderToken],
      }));
    }

    function isOrderCompleted(orderToken: string | null): boolean {
      return orderToken ? store.completedOrders().includes(orderToken) : false;
    }

    function clearCheckoutState() {
      const currentPaymentReference = store.paymentReference();
      const currentShowPaymentReference = store.showPaymentReference();
      const currentPaymentReferenceExpiry = store.paymentReferenceExpiry();

      patchState(store, {
        orderToken: null,
        currentPaymentMethodKey: null,
        paymentMethods: [],
        paymentStep: 'selection',
        selectedPaymentMethod: null,
        paymentReference: currentPaymentReference,
        showPaymentReference: currentShowPaymentReference,
        paymentReferenceExpiry: currentPaymentReferenceExpiry,
        pendingExternalRedirect: false,
      });
    }

    function persistOrderBeforeRedirect() {
      patchState(store, { pendingExternalRedirect: true });

      const currentOrder = store.order();
      const currentType = store.currentMethodState()?.type;
      if (currentOrder) {
        store._orderStorage.saveOrder(currentOrder);
      }
      if (currentType) {
        store._orderStorage.saveLastPaymentMethod(currentType);
      }

      // Durable PSP-redirect marker (issue #1180): the in-memory
      // `pendingExternalRedirect` flag dies on the `location.href` to the PSP,
      // so a back-button / direct-URL return to `/{token}` would have no
      // signal that a redirect happened. Persist it scoped by order token so
      // the paramless-return detection can offer seat release. Provider-
      // agnostic: this is the single redirect point, so all redirect PSPs
      // (Mercado Pago, Conekta, future Fintoc/Digitalfemsa) are covered.
      const token = store.orderToken();
      const redirect = store.redirectCheckoutData();
      if (token && redirect?.redirectUrl) {
        store._pspRedirectMarker.setMarker({
          orderToken: token,
          redirectUrl: redirect.redirectUrl,
          provider: redirect.provider ?? '',
        });
      }
    }

    // Navigates to /success immediately once a confirmation call starts (not
    // once it resolves), so the success page itself — not a dialog floating
    // over the checkout page — is what shows the processing/success
    // animation. checkoutSuccessGuard requires the order to already be in
    // OrderStorageService before it'll allow the navigation through, so the
    // order must be persisted here, synchronously, before navigating.
    function beginPaymentConfirmation(
      order: Order,
      paymentType?: PaymentMethodType,
    ) {
      const token = store.orderToken();
      if (!token) return;

      patchState(store, { confirmationOutcome: null });

      store._orderStorage.saveOrder(order);
      if (paymentType) {
        store._orderStorage.saveLastPaymentMethod(paymentType);
      }

      // Save the reference (if any, e.g. OXXO) before navigating too — the
      // success page's HubSpot tracking effect checks for an existing
      // reference on its first pass to decide whether the order is actually
      // paid yet; if this doesn't land before navigation, it would fire for
      // an unpaid OXXO order.
      const paymentReference = store.paymentReference();
      if (paymentReference) {
        store._referenceStorage.saveReference(paymentReference, token);
      }

      store._router.navigateByUrl(`/${token}/success`, { replaceUrl: true });
    }

    // Persists the completed-order bookkeeping once the order is confirmed —
    // by a confirm 200, or by a `pending`/`paid` read after a confirm error
    // (the charge went through even though confirm failed). Never for an
    // unsettled or failed order. Navigation already happened in
    // beginPaymentConfirmation, so this only finalizes state.
    function finalizeSuccessfulOrder(order?: Order) {
      const token = store.orderToken();
      const currentOrder =
        order ?? store.order() ?? store._orderStorage.order();
      const paymentReference = store.paymentReference();

      if (token && currentOrder) {
        store._orderStorage.saveOrder(currentOrder);

        const currentType = store.currentMethodState()?.type;
        if (currentType) {
          store._orderStorage.saveLastPaymentMethod(currentType);
        }

        if (paymentReference) {
          store._referenceStorage.saveReference(paymentReference, token);
        }

        markOrderCompleted(token);
        clearCheckoutState();
        // The order closed successfully — any redirect marker is obsolete.
        store._pspRedirectMarker.clearMarker();
      }
    }

    function handleExpiration() {
      const orderData = store.order();

      // The hold expired — the redirect marker is no longer meaningful.
      store._pspRedirectMarker.clearMarker();

      store._document.location.href = returnUrl(orderData);
    }

    function selectPaymentMethod(paymentMethod: PaymentMethod | null) {
      patchState(store, {
        selectedPaymentMethod: paymentMethod,
        paymentStep: paymentMethod ? 'payment' : 'selection',
      });
    }

    function backToSelection() {
      patchState(store, {
        paymentStep: 'selection',
        selectedPaymentMethod: null,
      });
    }

    function clearConektaCheckoutData() {
      patchState(store, (state) => ({
        paymentMethods: state.paymentMethods.map((method) =>
          method.provider === 'conekta'
            ? { ...method, checkoutData: undefined, initialized: false }
            : method,
        ),
        currentPaymentMethodKey: null,
      }));
    }

    function clearDigitalfemsaCheckoutData() {
      patchState(store, (state) => ({
        paymentMethods: state.paymentMethods.map((method) =>
          method.provider === 'digitalfemsa'
            ? { ...method, checkoutData: undefined, initialized: false }
            : method,
        ),
        currentPaymentMethodKey: null,
      }));
    }

    function clearRedirectCheckoutData() {
      patchState(store, (state) => ({
        paymentMethods: state.paymentMethods.map((method) =>
          method.checkoutData?.redirectUrl
            ? { ...method, checkoutData: undefined, initialized: false }
            : method,
        ),
        currentPaymentMethodKey: null,
        pendingExternalRedirect: false,
      }));
      // The user cancelled the redirect dialog — no PSP navigation happened, so
      // any marker written in persistOrderBeforeRedirect is stale and must not
      // survive (else a later paramless load would falsely offer release).
      store._pspRedirectMarker.clearMarker();
    }

    function setPaymentReference(paymentReference: PaymentReference) {
      patchState(store, {
        paymentReference,
        showPaymentReference: true,
        paymentReferenceExpiry: paymentReference.charge.expires_at,
      });
    }

    function clearPaymentReference() {
      patchState(store, {
        paymentReference: null,
        showPaymentReference: false,
        paymentReferenceExpiry: null,
      });
    }

    /**
     * Re-fetch the order from the server. The client's view of an order can go
     * stale without any local state changing — the hold expires server-side, or
     * another tab releases it — and a bfcache restore brings back that stale
     * view without re-bootstrapping the app.
     */
    function reloadOrder(): void {
      store._orderResource.reload();
    }

    function checkReferenceExpiry(): boolean {
      const expiry = store.paymentReferenceExpiry();
      if (expiry && Date.now() / 1000 > expiry) {
        clearPaymentReference();
        return true;
      }
      return false;
    }

    function dismissPspPopup(title: string) {
      patchState(store, (s) => ({
        pspDismissedPopupTitles: s.pspDismissedPopupTitles.includes(title)
          ? s.pspDismissedPopupTitles
          : [...s.pspDismissedPopupTitles, title],
      }));
      const win = store._document.defaultView;
      if (win) {
        let stored: string[] = [];
        try {
          const parsed = JSON.parse(
            win.localStorage.getItem('stx_psp_promotions_dismissed') ?? '[]',
          );
          if (Array.isArray(parsed)) stored = parsed;
        } catch {
          stored = [];
        }
        if (!stored.includes(title)) {
          stored.push(title);
          win.localStorage.setItem(
            'stx_psp_promotions_dismissed',
            JSON.stringify(stored),
          );
        }
      }
    }

    return {
      buildFanPortalUrl,
      returnUrl,
      setOrderToken,
      clearOrder,
      updatePaymentMethodState,
      setPaymentMethod,
      markOrderCompleted,
      isOrderCompleted,
      clearCheckoutState,
      persistOrderBeforeRedirect,
      beginPaymentConfirmation,
      finalizeSuccessfulOrder,
      handleExpiration,
      selectPaymentMethod,
      backToSelection,
      clearConektaCheckoutData,
      clearDigitalfemsaCheckoutData,
      clearRedirectCheckoutData,
      clearPaymentReference,
      reloadOrder,
      checkReferenceExpiry,
      setPaymentReference,
      buildConektaRedirectUrls,
      dismissPspPopup,
    };
  }),
  withMethods((store) => {
    const orderLoading$ = toObservable(store.orderLoading);

    function whenOrderSettles(): Observable<unknown> {
      return store.orderLoading()
        ? orderLoading$.pipe(first((loading) => !loading))
        : of(null);
    }

    function endWithoutConfirmation(
      outcome: OrderConfirmationOutcome,
    ): Observable<never> {
      store.setPaymentConfirmationError(
        ORDER_CONFIRMATION_OUTCOME_MESSAGES[outcome],
      );
      patchState(store, { confirmationOutcome: outcome });
      // No order was confirmed, but a payment the webhook may still settle
      // must not be offered again from the PSP-return dialog.
      store._pspRedirectMarker.clearMarker();
      return EMPTY;
    }

    /**
     * A confirm that did not answer 200 leaves the order's status as the only
     * truth about the payment. Poll it until it settles: `pending`/`paid`
     * emits the fresh order, `cancelled`/`failed` ends with the failure
     * outcome, and an order that never settles ends with the timeout panel.
     * `expired` keeps polling — the webhook can still pay an expired order
     * through its expired → cart recovery.
     *
     * A `cart` read means the lock holder let go without moving the order
     * (the expiry job found it still live, or an OXXO order nobody has paid
     * yet), so nothing else will confirm it: the tick confirms again. Confirm
     * is idempotent and a 409 just means the lock is held again. Any other
     * rejection stops the re-confirms — confirmPayment already retried it if
     * it was transient, the API will not answer differently next tick, and each attempt spends the
     * buyer's `orders` rate limit (10/min, shared with creating an order) — but
     * the reads go on, so a webhook can still settle the order. A wait that
     * starts from such a rejection never confirms at all (`reconfirm: false`).
     */
    function pollOrderConfirmation(
      order: Order,
      paymentMethod: PaymentMethodType,
      reconfirm: boolean,
    ): Observable<Order> {
      const token = store.orderToken();
      if (!token) {
        return throwError(() => new Error('No order token to poll'));
      }

      let reconfirmRejected = !reconfirm;

      return interval(ORDER_CONFIRMATION_POLL_INTERVAL_MS).pipe(
        exhaustMap(() =>
          store._checkoutApi.getOrderByToken(token).pipe(
            switchMap((polled) =>
              polled.status === 'cart' && !reconfirmRejected
                ? store._checkoutApi
                    .confirmPayment(order.id, paymentMethod)
                    .pipe(
                      ignoreElements(),
                      catchError((error) => {
                        reconfirmRejected ||= !isOrderProcessingConflict(error);
                        return EMPTY;
                      }),
                    )
                : of(polled),
            ),
            // A failed GET skips the tick — the next one retries.
            catchError(() => EMPTY),
          ),
        ),
        takeUntil(timer(ORDER_CONFIRMATION_POLL_WINDOW_MS)),
        first(
          (polled) =>
            isFailedOrderStatus(polled.status) ||
            polled.status === 'pending' ||
            polled.status === 'paid',
          null,
        ),
        switchMap((polled) => {
          if (!polled) {
            return endWithoutConfirmation('timeout');
          }
          if (isFailedOrderStatus(polled.status)) {
            return endWithoutConfirmation('cancelled');
          }
          return of(polled);
        }),
      );
    }

    /**
     * Confirm, turning any error into the wait for the order to settle — a
     * settled order flows into the caller's success path exactly like a
     * confirm 200. Confirm never charges: a rejection only says the order
     * could not move to `pending` (a webhook may have cancelled it first, or
     * paid it), so the order's status decides, never the error itself.
     *
     * Emits the polled order after an error, `undefined` after a 200: the
     * confirm body is the bare booking payload (no seats, event or formatted
     * totals), never the order the success page renders.
     */
    function confirmOrAwaitSettlement(
      order: Order,
      paymentMethod: PaymentMethodType,
    ): Observable<Order | undefined> {
      return store._checkoutApi.confirmPayment(order.id, paymentMethod).pipe(
        map(() => undefined),
        catchError((error) =>
          pollOrderConfirmation(
            order,
            paymentMethod,
            isOrderProcessingConflict(error),
          ),
        ),
      );
    }

    /**
     * The outcome of a confirmation lives only in memory, so a reload of the
     * success page has none and would render "Pago confirmado" for whatever
     * order it finds. Reading the order restores it: a cancelled order is the
     * failure outcome, and one confirm never moved out of `cart`/`expired` is
     * still waiting on its payment — the timeout outcome. Only a booked order,
     * or a failed read, leaves the page as it renders today.
     */
    function recoverConfirmationOutcome(token: string): Observable<never> {
      return store._checkoutApi.getOrderByToken(token).pipe(
        switchMap((order) => {
          if (isFailedOrderStatus(order.status)) {
            return endWithoutConfirmation('cancelled');
          }
          if (order.status === 'cart' || order.status === 'expired') {
            return endWithoutConfirmation('timeout');
          }
          return EMPTY;
        }),
        catchError(() => EMPTY),
      );
    }

    return {
      recoverConfirmationOutcome,
      releaseAndReturnToSeats: rxMethod<void>(
        pipe(
          // A PSP failure dialog can close before the order's first read
          // lands. The stored fallback was saved before the redirect, so it
          // always says `cart`: only the server's status may decide.
          switchMap(() => whenOrderSettles()),
          switchMap(() => {
            const order = store.order() ?? store._orderStorage.order();
            // Releasing the seats ends the redirect lifecycle for this order.
            store._pspRedirectMarker.clearMarker();
            const seatSelectionUrl = store.returnUrl(order);

            // Only a `cart` order holds seats the API will release. Past it the
            // PSP's webhook frees them if the payment fails, and a release
            // would only answer 409 and keep the buyer here.
            if (!order?.id || order.status !== 'cart') {
              store._document.location.href = seatSelectionUrl;
              return EMPTY;
            }

            const loadingRef = store._loadingService.show({
              message: 'Liberando asientos...',
              cancelable: false,
            });

            return store._checkoutApi.releaseSeats(order.id).pipe(
              tap(() => {
                store.clearOrder();
                store._document.location.href = seatSelectionUrl;
              }),
              // Whatever the status, a failed release means the seats may
              // still be held — or the order already moved on. Keep the buyer
              // here and let the order's real status decide what they see.
              catchError(() => {
                store._alert.showError(RELEASE_SEATS_FAILED_MESSAGE);
                store.reloadOrder();
                return EMPTY;
              }),
              finalize(() => loadingRef.close()),
            );
          }),
        ),
      ),
      /**
       * Re-open a PSP payment session, but only if the server still says the
       * order can be paid. The order may have expired or been released since
       * the redirect URL was recorded, and the marker is a trigger, not an
       * authority — `status === 'cart'` read fresh from the server is.
       *
       * On any invalid outcome the marker is dropped and the order resource is
       * reloaded, which surfaces the existing "orden no disponible" flow
       * instead of sending the user to a dead payment session.
       */
      continuePspPayment: rxMethod<{
        token: string;
        redirectUrl: string;
        /**
         * Called when the order could not be verified but the marker was
         * kept — the offer is still live and the caller may re-expose it.
         */
        onUnverified?: () => void;
      }>(
        pipe(
          switchMap(({ token, redirectUrl, onUnverified }) =>
            store._checkoutApi.getOrderByToken(token).pipe(
              tap((order) => {
                const expired =
                  new Date(order.expires_at).getTime() <= Date.now();

                if (order.status === 'cart' && !expired) {
                  store._document.location.href = redirectUrl;
                  return;
                }

                store._pspRedirectMarker.clearMarker();
                store._alert.showWarning(
                  expired
                    ? 'Tu reserva expiró. No es posible continuar con este pago.'
                    : 'Esta orden ya no está disponible para pago.',
                );
                store.reloadOrder();
              }),
              catchError((error: HttpErrorResponse) => {
                // Only the server saying the order is gone justifies dropping
                // the marker — it is irreversible, and the buyer still holds
                // seats and may have a payment in flight at the PSP. A 500 or
                // an offline blip is retryable, and this flow runs precisely
                // where the network is worst: back from a bank app on mobile.
                if (error.status === 404 || error.status === 403) {
                  store._pspRedirectMarker.clearMarker();
                  store.reloadOrder();
                } else {
                  store._alert.showError(
                    'No pudimos verificar tu orden. Intenta de nuevo.',
                  );
                  onUnverified?.();
                }
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      applyCoupon: rxMethod<{ code: string; referenceCode?: string }>(
        pipe(
          switchMap(({ code, referenceCode }) => {
            const order = store.order();
            if (!order) return EMPTY;

            store.setCouponLoading();

            return store._checkoutApi
              .applyCoupon(order.id, code, referenceCode)
              .pipe(
                tap(() => {
                  patchState(store, {
                    appliedCouponCode: code,
                    pendingCouponCode: null,
                    referenceConfig: null,
                  });
                  store._orderResource.reload();
                  store.setCouponLoaded();
                  store._alert.showSuccess('Cupón aplicado');
                }),
                catchError((error: HttpErrorResponse) => {
                  if (error.status === 428) {
                    patchState(store, {
                      pendingCouponCode: code,
                      referenceConfig:
                        error.error?.error?.reference_config ?? null,
                    });
                    store.setCouponLoaded();
                  } else {
                    const msg =
                      error?.error?.message ?? 'Cupón inválido o expirado';
                    store.setCouponError(msg);
                  }
                  return EMPTY;
                }),
              );
          }),
        ),
      ),
      removeCoupon: rxMethod<void>(
        pipe(
          switchMap(() => {
            const order = store.order();
            if (!order) return EMPTY;

            store.setCouponLoading();

            return store._checkoutApi.removeCoupon(order.id).pipe(
              tap(() => {
                patchState(store, {
                  appliedCouponCode: null,
                  pendingCouponCode: null,
                  referenceConfig: null,
                });
                store._orderResource.reload();
                store.setCouponLoaded();
              }),
              catchError(() => {
                store.setCouponError('Error al remover el cupón');
                return EMPTY;
              }),
            );
          }),
        ),
      ),
      markPendingRedirect: () =>
        patchState(store, { pendingExternalRedirect: true }),
      clearMercadopagoFailure: () =>
        patchState(store, { mercadopagoFailureStatus: null }),
      clearConektaReturn: () =>
        patchState(store, { conektaReturnStatus: null }),
      handleConektaReturn: rxMethod<ConektaReturnResult>(
        pipe(
          switchMap((result) => {
            const order = store.order() ?? store._orderStorage.order();
            if (!order) {
              store._alert.showError(
                'No se encontró la orden. Contacta soporte.',
              );
              return EMPTY;
            }
            store._orderStorage.saveOrder(order);

            if (result === 'cancel') {
              patchState(store, { conektaReturnStatus: 'cancel' });
              return EMPTY;
            }

            // success → confirm with backend (mirrors handleMercadopagoReturn)
            const conektaCardKey = store
              .paymentMethods()
              .find((m) => m.provider === 'conekta' && m.type === 'card')?.key;
            patchState(store, {
              currentPaymentMethodKey: conektaCardKey ?? 'conekta:card',
            });

            store.setPaymentConfirmationLoading();
            store.beginPaymentConfirmation(order, 'card');

            return confirmOrAwaitSettlement(order, 'card').pipe(
              tap((settledOrder) => {
                store.setPaymentConfirmationLoaded();
                store.finalizeSuccessfulOrder(settledOrder);
              }),
              catchError((error) => {
                store.setPaymentConfirmationError(
                  error?.userMessage || 'Error al confirmar la orden',
                );
                return EMPTY;
              }),
            );
          }),
        ),
      ),
      initializePayment: rxMethod<string>(
        pipe(
          switchMap((methodKey) => {
            const order = store.order();
            if (!order) {
              store._alert.showWarning(
                'No hay una orden activa. Por favor, intenta nuevamente.',
              );
              return EMPTY;
            }

            if (store.isExpired()) {
              store._alert.showWarning('Tu reserva ha expirado.');
              store.handleExpiration();
              return EMPTY;
            }

            const method = store
              .paymentMethods()
              .find((m) => m.key === methodKey);
            if (!method) return EMPTY;

            if (method.initialized && method.checkoutData) {
              patchState(store, { currentPaymentMethodKey: methodKey });
              return EMPTY;
            }

            patchState(store, { currentPaymentMethodKey: methodKey });
            store.setPaymentLoading();

            const selectedMethods: PaymentMethodType[] = [method.type];

            patchState(store, (state) => ({
              paymentMethods: state.paymentMethods.map((m) =>
                m.key === methodKey ? { ...m, initialized: true } : m,
              ),
            }));

            const methodProvider = method.provider;

            return store._checkoutApi
              .initializePaymentMethods(
                order.id,
                selectedMethods,
                methodProvider === 'conekta'
                  ? store.buildConektaRedirectUrls()
                  : undefined,
              )
              .pipe(
                tap((response: MultiPaymentInitializeResponse) => {
                  Object.entries(response).forEach(([provider, data]) => {
                    if (data && provider === methodProvider) {
                      store.updatePaymentMethodState(methodKey, {
                        checkoutData: {
                          checkout_id: data.provider_checkout_id,
                          provider_checkout_id: data.provider_checkout_id,
                          provider_order_id: data.provider_order_id,
                          redirectUrl: data.checkout_url,
                          provider,
                          status: 'pending',
                        },
                      });
                    }
                  });
                  store.setPaymentLoaded();
                }),
                catchError((error) => {
                  const errorMsg =
                    error?.userMessage ||
                    'Error al inicializar el método de pago';
                  store._alert.showError(errorMsg);
                  store.setPaymentError(errorMsg);
                  patchState(store, (state) => ({
                    paymentMethods: state.paymentMethods.map((m) =>
                      m.key === methodKey ? { ...m, initialized: false } : m,
                    ),
                    currentPaymentMethodKey: null,
                  }));
                  return EMPTY;
                }),
              );
          }),
        ),
      ),
      initializeMultiplePayments: rxMethod<PaymentMethodType[]>(
        pipe(
          switchMap((methodIds) => {
            const order = store.order();
            if (!order) {
              store._alert.showWarning(
                'No hay una orden activa. Por favor, intenta nuevamente.',
              );
              return EMPTY;
            }

            if (store.isExpired()) {
              store._alert.showWarning('Tu reserva ha expirado.');
              store.handleExpiration();
              return EMPTY;
            }

            const methods = store
              .paymentMethods()
              .filter((m) => methodIds.includes(m.id));
            const allInitialized = methods.every(
              (m) => m.initialized && m.checkoutData,
            );

            if (allInitialized) {
              return EMPTY;
            }

            store.setPaymentLoading();

            patchState(store, (state) => ({
              paymentMethods: state.paymentMethods.map((method) =>
                methodIds.includes(method.id)
                  ? { ...method, initialized: true }
                  : method,
              ),
            }));

            const hasConekta = methods.some((m) => m.provider === 'conekta');

            return store._checkoutApi
              .initializePaymentMethods(
                order.id,
                methodIds,
                hasConekta ? store.buildConektaRedirectUrls() : undefined,
              )
              .pipe(
                tap((response: MultiPaymentInitializeResponse) => {
                  const methods = store.paymentMethods();
                  Object.entries(response).forEach(([provider, data]) => {
                    if (!data) return;
                    const matchingMethods = methods.filter(
                      (m) =>
                        m.provider === provider && methodIds.includes(m.id),
                    );
                    matchingMethods.forEach((m) => {
                      store.updatePaymentMethodState(m.key, {
                        checkoutData: {
                          checkout_id: data.provider_checkout_id,
                          provider_checkout_id: data.provider_checkout_id,
                          provider_order_id: data.provider_order_id,
                          redirectUrl: data.checkout_url,
                          provider,
                          status: 'pending',
                        },
                      });
                    });
                  });
                  store.setPaymentLoaded();
                }),
                catchError((error) => {
                  const errorMsg =
                    error?.userMessage ||
                    'Error al inicializar los métodos de pago';
                  store._alert.showError(errorMsg);
                  store.setPaymentError(errorMsg);

                  patchState(store, (state) => ({
                    paymentMethods: state.paymentMethods.map((method) =>
                      methodIds.includes(method.id)
                        ? { ...method, initialized: false }
                        : method,
                    ),
                  }));

                  return EMPTY;
                }),
              );
          }),
        ),
      ),
      confirmPayment: rxMethod<void>(
        pipe(
          switchMap(() => {
            const order = store.order();
            if (!order) {
              store._alert.showError(
                'Error: No hay orden activa para confirmar',
              );
              return EMPTY;
            }

            store.setPaymentConfirmationLoading();
            const paymentMethod = store.currentMethodState()
              ?.type as PaymentMethodType;
            store.beginPaymentConfirmation(order, paymentMethod);

            return confirmOrAwaitSettlement(order, paymentMethod).pipe(
              tap((settledOrder) => {
                store._alert.showSuccess('¡Orden confirmada exitosamente!');
                store.setPaymentConfirmationLoaded();
                store.finalizeSuccessfulOrder(settledOrder);
              }),
              catchError((error) => {
                store.setPaymentConfirmationError(
                  error?.userMessage || 'Error al confirmar la orden',
                );
                return EMPTY;
              }),
            );
          }),
        ),
      ),
      setConektaInizialized: (isInitialized = true) =>
        patchState(store, {
          paymentMethods: store
            .paymentMethods()
            .map((method) =>
              method.provider === 'conekta' && method.type === 'card'
                ? { ...method, initialized: isInitialized }
                : method,
            ),
        }),
      setDigitalfemsaInitialized: (isInitialized = true) =>
        patchState(store, {
          paymentMethods: store
            .paymentMethods()
            .map((method) =>
              method.provider === 'digitalfemsa' && method.type === 'oxxo'
                ? { ...method, initialized: isInitialized }
                : method,
            ),
        }),
      handleMercadopagoReturn: rxMethod<MercadoPagoReturnParams>(
        pipe(
          switchMap((params) => {
            // Use localStorage order since httpResource hasn't resolved yet after redirect
            const order = store.order() ?? store._orderStorage.order();
            if (!order) {
              store._alert.showError(
                'No se encontró la orden. Contacta soporte.',
              );
              return EMPTY;
            }

            if (params.mp_result === 'failure') {
              const failureStatus =
                params.status === 'rejected' || params.status === 'cancelled'
                  ? params.status
                  : 'rejected';
              patchState(store, { mercadopagoFailureStatus: failureStatus });
              return EMPTY;
            }

            // approved, pending, in_process → confirm with backend
            const mpCardKey = store
              .paymentMethods()
              .find(
                (m) => m.provider === 'mercadopago' && m.type === 'card',
              )?.key;
            patchState(store, {
              currentPaymentMethodKey: mpCardKey ?? 'mercadopago:card',
            });
            store.setPaymentConfirmationLoading();
            store.beginPaymentConfirmation(order, 'card');

            return confirmOrAwaitSettlement(order, 'card').pipe(
              tap((settledOrder) => {
                store.setPaymentConfirmationLoaded();
                store.finalizeSuccessfulOrder(settledOrder);
              }),
              catchError((error) => {
                store.setPaymentConfirmationError(
                  error?.userMessage || 'Error al confirmar la orden',
                );
                return EMPTY;
              }),
            );
          }),
        ),
      ),
    };
  }),
  withAlert(),
  withHooks({
    onInit: (store) => {
      const win = store._document.defaultView;
      if (win) {
        try {
          const raw = win.localStorage.getItem('stx_psp_promotions_dismissed');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length) {
              patchState(store, { pspDismissedPopupTitles: parsed });
            }
          }
        } catch {
          // ignore malformed localStorage value
        }
      }

      effect(() => {
        const order = store.order();
        if (!order?.allowed_payment_methods) return;

        const currentMethods = store.paymentMethods();
        if (currentMethods.length > 0) return;

        const methods = buildPaymentMethodsFromOrder(
          order.allowed_payment_methods,
        );
        if (methods.length > 0) {
          patchState(store, { paymentMethods: methods });
        }
      });

      effect(() => {
        if (store.pendingExternalRedirect()) {
          return;
        }

        // A confirmation in flight (including the post-409 poll) owns the
        // navigation, and so does the outcome panel it ends on: the hold can
        // expire while the webhook is still paying the order, and bouncing
        // the buyer back would kill the outcome they are waiting for.
        if (
          store.isPaymentConfirmationLoading() ||
          store.confirmationOutcome()
        ) {
          return;
        }

        if (store.isExpired() && store.order()) {
          store._alert.showWarning('Tu reserva ha expirado.');
          store.handleExpiration();
        }
      });
    },
  }),
);
